import React, { useState } from 'react';
import {
  Scissors,
  Maximize2,
  Save,
  Sliders,
  Eraser,
  Stamp,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { ResizeMethodSelector } from '../resize/ResizeMethodSelector';
import { DimensionResize } from '../resize/DimensionResize';
import { SizeResize } from '../resize/SizeResize';
import { OutputControls } from '../output/OutputControls';
import { ImageAdjustments, ImageAdjustmentsState, INITIAL_ADJUSTMENTS } from './ImageAdjustments';
import { BackgroundRemoval } from './BackgroundRemoval';
import { WatermarkEditor, WatermarkState } from './WatermarkEditor';
import { PresetSelector, ImagePreset } from '../resize/PresetSelector';
import { ExifViewer } from '../privacy/ExifViewer';
import type { ResizeMethod } from '../../types/resize';

type AccordionSection = 'resize' | 'crop' | 'format' | 'adjust' | 'bg' | 'watermark' | 'exif';

interface AccordionControlGroupProps {
  originalImage: File;
  processedImage: string | null;
  resizeMethod: ResizeMethod;
  setResizeMethod: (method: ResizeMethod) => void;
  outputFormat: string;
  outputQuality: number;
  setOutputFormat: (fmt: string) => void;
  setOutputQuality: (q: number) => void;
  lastDimensions?: { width: number; height: number };
  activePresetId: string | null;
  adjustments: ImageAdjustmentsState;
  setAdjustments: (adj: ImageAdjustmentsState) => void;
  watermark: WatermarkState;
  setWatermark: (w: WatermarkState) => void;
  setShowCropInterface: (show: boolean) => void;
  handleProcess: (url: string) => void;
  setIsProcessing: (proc: boolean) => void;
  handlePresetSelect: (preset: ImagePreset) => void;
  handleAdjustPresetCrop: (preset: ImagePreset) => void;
  setProcessedImage: (url: string) => void;
}

export function AccordionControlGroup({
  originalImage,
  processedImage,
  resizeMethod,
  setResizeMethod,
  outputFormat,
  outputQuality,
  setOutputFormat,
  setOutputQuality,
  lastDimensions,
  activePresetId,
  adjustments,
  setAdjustments,
  watermark,
  setWatermark,
  setShowCropInterface,
  handleProcess,
  setIsProcessing,
  handlePresetSelect,
  handleAdjustPresetCrop,
  setProcessedImage,
}: AccordionControlGroupProps) {
  // Only ONE section opens at a time (defaults to 'resize')
  const [activeSection, setActiveSection] = useState<AccordionSection | null>('resize');

  const toggleSection = (section: AccordionSection) => {
    setActiveSection((prev) => (prev === section ? null : section));
  };

  const formatLabel = outputFormat.split('/')[1]?.toUpperCase() || 'JPEG';

  return (
    <div className="space-y-2.5">
      {/* 1. Resize & Dimensions Accordion */}
      <div className="bg-slate-50 dark:bg-slate-950/70 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection('resize')}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100/50 dark:hover:bg-slate-900/50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <Maximize2 className="w-4 h-4 text-indigo-500 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Resize & Dimensions
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono font-bold px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300">
              {lastDimensions ? `${lastDimensions.width}×${lastDimensions.height}px` : 'Custom'}
            </span>
            {activeSection === 'resize' ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>

        {activeSection === 'resize' && (
          <div className="p-4 pt-1 border-t border-slate-200/60 dark:border-slate-800/60 space-y-3 animate-in fade-in duration-150">
            <ResizeMethodSelector method={resizeMethod} onChange={setResizeMethod} />
            {resizeMethod === 'dimension' && (
              <DimensionResize
                originalImage={originalImage}
                onProcess={handleProcess}
                onProcessingChange={setIsProcessing}
                outputFormat={outputFormat}
                outputQuality={outputQuality}
                initialDimensions={lastDimensions}
              />
            )}
            {resizeMethod === 'size' && (
              <SizeResize
                originalImage={originalImage}
                onProcess={handleProcess}
                onProcessingChange={setIsProcessing}
                outputFormat={outputFormat}
              />
            )}
          </div>
        )}
      </div>

      {/* 2. Crop & Aspect Presets Accordion */}
      <div className="bg-slate-50 dark:bg-slate-950/70 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection('crop')}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100/50 dark:hover:bg-slate-900/50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <Scissors className="w-4 h-4 text-indigo-500 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Crop & Aspect Presets
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {activePresetId ? 'Preset Active' : 'Manual'}
            </span>
            {activeSection === 'crop' ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>

        {activeSection === 'crop' && (
          <div className="p-4 pt-1 border-t border-slate-200/60 dark:border-slate-800/60 space-y-4 animate-in fade-in duration-150">
            <button
              type="button"
              onClick={() => setShowCropInterface(true)}
              className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
            >
              <Scissors className="w-4 h-4" />
              <span>Launch Interactive Visual Crop Tool</span>
            </button>

            <PresetSelector
              activePresetId={activePresetId}
              onSelectPreset={handlePresetSelect}
              onAdjustCrop={handleAdjustPresetCrop}
            />
          </div>
        )}
      </div>

      {/* 3. Output Format & Quality Accordion */}
      <div className="bg-slate-50 dark:bg-slate-950/70 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection('format')}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100/50 dark:hover:bg-slate-900/50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <Save className="w-4 h-4 text-indigo-500 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Format & Compression Quality
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300">
              {formatLabel} {outputFormat !== 'image/png' ? `${outputQuality}%` : ''}
            </span>
            {activeSection === 'format' ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>

        {activeSection === 'format' && (
          <div className="p-4 pt-1 border-t border-slate-200/60 dark:border-slate-800/60 space-y-3 animate-in fade-in duration-150">
            <OutputControls
              resizeMethod={resizeMethod}
              format={outputFormat}
              quality={outputQuality}
              onFormatChange={setOutputFormat}
              onQualityChange={setOutputQuality}
              currentDimensions={lastDimensions}
            />
          </div>
        )}
      </div>

      {/* 4. Transform & Color Adjustments Accordion */}
      <div className="bg-slate-50 dark:bg-slate-950/70 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection('adjust')}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100/50 dark:hover:bg-slate-900/50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <Sliders className="w-4 h-4 text-indigo-500 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Transform & Color Filters
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {adjustments.rotation !== 0 || adjustments.flipH || adjustments.flipV ? 'Modified' : 'Default'}
            </span>
            {activeSection === 'adjust' ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>

        {activeSection === 'adjust' && (
          <div className="p-4 pt-1 border-t border-slate-200/60 dark:border-slate-800/60 space-y-3 animate-in fade-in duration-150">
            <ImageAdjustments
              adjustments={adjustments}
              onChange={setAdjustments}
              onReset={() => setAdjustments(INITIAL_ADJUSTMENTS)}
            />
          </div>
        )}
      </div>

      {/* 5. AI Background Remover & Keyer Accordion */}
      <div className="bg-slate-50 dark:bg-slate-950/70 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection('bg')}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100/50 dark:hover:bg-slate-900/50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <Eraser className="w-4 h-4 text-pink-500 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Background Remover & Keyer
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-pink-100 dark:bg-pink-950/80 text-pink-700 dark:text-pink-300">
              100% Local
            </span>
            {activeSection === 'bg' ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>

        {activeSection === 'bg' && (
          <div className="p-4 pt-1 border-t border-slate-200/60 dark:border-slate-800/60 space-y-3 animate-in fade-in duration-150">
            <BackgroundRemoval
              originalImage={originalImage}
              currentProcessedUrl={processedImage}
              onApplyBackgroundRemoval={(dataUrl) => setProcessedImage(dataUrl)}
            />
          </div>
        )}
      </div>

      {/* 6. Text Watermark Overlay Accordion */}
      <div className="bg-slate-50 dark:bg-slate-950/70 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection('watermark')}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100/50 dark:hover:bg-slate-900/50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <Stamp className="w-4 h-4 text-indigo-500 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Add Text Watermark Overlay
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
              {watermark.text ? 'Active' : 'Disabled'}
            </span>
            {activeSection === 'watermark' ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>

        {activeSection === 'watermark' && (
          <div className="p-4 pt-1 border-t border-slate-200/60 dark:border-slate-800/60 space-y-3 animate-in fade-in duration-150">
            <WatermarkEditor watermark={watermark} onChange={setWatermark} />
          </div>
        )}
      </div>

      {/* 7. Privacy & EXIF Metadata Accordion */}
      <div className="bg-slate-50 dark:bg-slate-950/70 rounded-2xl border border-slate-200/80 dark:border-slate-800 overflow-hidden shadow-sm">
        <button
          type="button"
          onClick={() => toggleSection('exif')}
          className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-100/50 dark:hover:bg-slate-900/50 transition-colors"
        >
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
              Privacy & EXIF Metadata
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300">
              Protected
            </span>
            {activeSection === 'exif' ? (
              <ChevronUp className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </button>

        {activeSection === 'exif' && (
          <div className="p-4 pt-1 border-t border-slate-200/60 dark:border-slate-800/60 space-y-3 animate-in fade-in duration-150">
            <ExifViewer file={originalImage} dimensions={lastDimensions} />
          </div>
        )}
      </div>
    </div>
  );
}
